/**
 * Canonical state waste code data.
 *
 * Single source of truth for RCRAInfo / eManifest state waste code validation and
 * classification. Each entry carries `value`, `definition`, and `classification` so that
 * every consumer — validators, classifiers, exporters, the CA chemical-state-code enum,
 * and downstream forms — picks up the same answer on the same deploy.
 *
 * Edits are TypeScript edits: a code added here is accepted by `isValidStateWasteCode`
 * and classified by `getStateWasteCodeClassification` on the same deploy with no DB
 * migration, no cache restart, and no per-process warm-up.
 *
 * Source-of-record: this file. The legacy `StateWasteCode` DB table has been dropped
 * (ENG-15211). The `definition` strings were migrated verbatim from the release DB
 * snapshot on 2026-05-12 to preserve all prior team edits.
 */

import { isFederalAcutelyHazardousCode, isFederalWasteCode } from "./federalWasteCodes";

export type StateWasteClassification = "HAZARDOUS" | "NONHAZARDOUS" | "ACUTELY_HAZARDOUS";

export interface StateWasteCodeEntry {
  readonly value: string;
  readonly classification: StateWasteClassification;
  readonly definition: string;
  /**
   * Whether the code should be treated as subject to e-Manifest. When omitted, the
   * value is derived from `classification` (HAZARDOUS / ACUTELY_HAZARDOUS → true,
   * NONHAZARDOUS → false).
   *
   * Two kinds of explicit override exist today, both marked with adjacent comments:
   *  - **EPA-confirmed** — EPA's published list disagrees with the canonical
   *    classification (e.g. MA99: NONHAZ but listed by EPA → override `true`).
   *  - **Transitional / behavior-preservation** — EPA does not list the code,
   *    but today's validation-based filter accepts it, so an override is
   *    required to preserve behavior once `isSubjectToEManifest` replaces the
   *    classification-derived gate. Pending waste-team review.
   *
   * EPA source-of-record:
   * https://www.epa.gov/e-manifest/state-only-hazardous-waste-subject-resource-conservation-and-recovery-act-manifests
   * Cross-reference captured at docs/investigations/2026-05-26-132758-ENG-15502-investigation.md.
   */
  readonly subjectToEManifest?: boolean;
}

export const STATE_WASTE_CODES: Readonly<Record<string, readonly StateWasteCodeEntry[]>> = {
  // CA — California — Non-RCRA Hazardous Waste.
  CA: [
    {
      value: "121",
      classification: "HAZARDOUS",
      definition:
        "Alkaline solution (pH >12.5) with metals (antimony, arsenic, barium, beryllium, cadmium, chromium, c cobalt, copper, lead, mercury, molybdenum, nickel, selenium, silver, thallium, vanadium, and zinc)",
    },
    {
      value: "122",
      classification: "HAZARDOUS",
      definition: "Alkaline solution without metals (pH > 12.5)",
    },
    {
      value: "123",
      classification: "HAZARDOUS",
      definition: "Unspecified alkaline solution",
    },
    {
      value: "131",
      classification: "HAZARDOUS",
      definition:
        "Aqueous solution (2 < pH < 12.5) containing reactive anions (azide, bromate, chlorate, cyanide, fluoride, hypochlorite, nitrite, perchlorate, and sulfide anions)",
    },
    {
      value: "132",
      classification: "HAZARDOUS",
      definition:
        "Aqueous solution w/metals (< restricted levels and see waste code 121 for a list of metals)",
    },
    {
      value: "133",
      classification: "HAZARDOUS",
      definition: "Aqueous solution with 10% or more total organic residues",
    },
    {
      value: "134",
      classification: "HAZARDOUS",
      definition: "Aqueous solution with <10% total organic residues",
    },
    {
      value: "135",
      classification: "HAZARDOUS",
      definition: "Unspecified aqueous solution",
    },
    {
      value: "141",
      classification: "HAZARDOUS",
      definition: "Off-specification, aged, or surplus inorganics",
    },
    {
      value: "151",
      classification: "HAZARDOUS",
      definition: "Asbestos-containing waste",
    },
    {
      value: "161",
      classification: "HAZARDOUS",
      definition: "Fluid-cracking catalyst (FCC) waste",
    },
    {
      value: "162",
      classification: "HAZARDOUS",
      definition: "Other spent catalyst",
    },
    {
      value: "171",
      classification: "HAZARDOUS",
      definition: "Metal sludge (see 121)",
    },
    {
      value: "172",
      classification: "HAZARDOUS",
      definition: "Metal dust (see 121) and machining waste",
    },
    {
      value: "181",
      classification: "HAZARDOUS",
      definition: "Other inorganic solid waste",
    },
    {
      value: "211",
      classification: "HAZARDOUS",
      definition: "Halogenated solvents (chloroform, methyl chloride, perchloroethylene, etc.)",
    },
    {
      value: "212",
      classification: "HAZARDOUS",
      definition: "Oxygenated solvents (acetone, butanol, ethyl acetate, etc.)",
    },
    {
      value: "213",
      classification: "HAZARDOUS",
      definition: "Hydrocarbon solvents (benzene, hexane, Stoddard, etc.)",
    },
    {
      value: "214",
      classification: "HAZARDOUS",
      definition: "Unspecified solvent mixture",
    },
    {
      value: "221",
      classification: "HAZARDOUS",
      definition: "Waste oil and mixed oil",
    },
    {
      value: "222",
      classification: "HAZARDOUS",
      definition: "Oil/water separation sludge",
    },
    {
      value: "223",
      classification: "HAZARDOUS",
      definition: "Unspecified oil-containing waste",
    },
    {
      value: "231",
      classification: "HAZARDOUS",
      definition: "Pesticide rinse water",
    },
    {
      value: "232",
      classification: "HAZARDOUS",
      definition: "Pesticides and other waste associated with pesticide production",
    },
    {
      value: "241",
      classification: "HAZARDOUS",
      definition: "Tank bottom waste",
    },
    {
      value: "251",
      classification: "HAZARDOUS",
      definition: "Still bottoms with halogenated organics",
    },
    {
      value: "252",
      classification: "HAZARDOUS",
      definition: "Other still bottom waste",
    },
    {
      value: "261",
      classification: "HAZARDOUS",
      definition: "Polychlorinated biphenyls and material containing PCBs",
    },
    {
      value: "271",
      classification: "HAZARDOUS",
      definition: "Organic monomer waste (includes unreacted resins)",
    },
    {
      value: "272",
      classification: "HAZARDOUS",
      definition: "Polymeric resin waste",
    },
    { value: "281", classification: "HAZARDOUS", definition: "Adhesives" },
    { value: "291", classification: "HAZARDOUS", definition: "Latex waste" },
    {
      value: "311",
      classification: "HAZARDOUS",
      definition: "Pharmaceutical waste",
    },
    { value: "321", classification: "HAZARDOUS", definition: "Sewage sludge" },
    {
      value: "322",
      classification: "HAZARDOUS",
      definition: "Biological waste other than sewage sludge",
    },
    {
      value: "331",
      classification: "HAZARDOUS",
      definition: "Off-specification, aged, or surplus organics",
    },
    {
      value: "341",
      classification: "HAZARDOUS",
      definition: "Organic liquids (non-solvents) with halogens",
    },
    {
      value: "342",
      classification: "HAZARDOUS",
      definition: "Organic liquids with metals (see 121)",
    },
    {
      value: "343",
      classification: "HAZARDOUS",
      definition: "Unspecified organic liquid mixture",
    },
    {
      value: "351",
      classification: "HAZARDOUS",
      definition: "Organic solids with halogens",
    },
    {
      value: "352",
      classification: "HAZARDOUS",
      definition: "Other organic solids",
    },
    {
      value: "411",
      classification: "HAZARDOUS",
      definition: "Alum and gypsum sludge",
    },
    { value: "421", classification: "HAZARDOUS", definition: "Lime sludge" },
    {
      value: "431",
      classification: "HAZARDOUS",
      definition: "Phosphate sludge",
    },
    { value: "441", classification: "HAZARDOUS", definition: "Sulfur sludge" },
    {
      value: "451",
      classification: "HAZARDOUS",
      definition: "Degreasing sludge",
    },
    { value: "461", classification: "HAZARDOUS", definition: "Paint sludge" },
    {
      value: "471",
      classification: "HAZARDOUS",
      definition: "Paper sludge/pulp",
    },
    {
      value: "481",
      classification: "HAZARDOUS",
      definition: "Tetraethyl lead sludge",
    },
    {
      value: "491",
      classification: "HAZARDOUS",
      definition: "Unspecified sludge waste",
    },
    {
      value: "511",
      classification: "HAZARDOUS",
      definition: "Empty pesticide containers 30 gallons or more",
    },
    {
      value: "512",
      classification: "HAZARDOUS",
      definition: "Other empty containers 30 gallons or more",
    },
    {
      value: "513",
      classification: "HAZARDOUS",
      definition: "Empty containers less than 30 gallons",
    },
    { value: "521", classification: "HAZARDOUS", definition: "Drilling mud" },
    {
      value: "531",
      classification: "HAZARDOUS",
      definition: "Chemical toilet waste",
    },
    {
      value: "541",
      classification: "HAZARDOUS",
      definition: "Photo chemicals / photo processing waste",
    },
    {
      value: "551",
      classification: "HAZARDOUS",
      definition: "Laboratory waste chemicals",
    },
    {
      value: "561",
      classification: "HAZARDOUS",
      definition: "Detergent and soap",
    },
    {
      value: "571",
      classification: "HAZARDOUS",
      definition: "Fly ash, bottom ash, and retort ash",
    },
    {
      value: "581",
      classification: "HAZARDOUS",
      definition: "Gas scrubber waste",
    },
    { value: "591", classification: "HAZARDOUS", definition: "Baghouse waste" },
    {
      value: "611",
      classification: "HAZARDOUS",
      definition: "Contaminated soil from site clean-ups",
    },
    {
      value: "612",
      classification: "HAZARDOUS",
      definition: "Household waste",
    },
    {
      value: "613",
      classification: "HAZARDOUS",
      definition: "Auto shredder waste",
    },
    {
      value: "614",
      classification: "HAZARDOUS",
      definition: "Treated wood waste",
    },
    {
      value: "711",
      classification: "HAZARDOUS",
      definition: "Liquids with cyanides > 1000 mg/l",
    },
    {
      value: "721",
      classification: "HAZARDOUS",
      definition: "Liquids with arsenic > 500 mg/l",
    },
    {
      value: "722",
      classification: "HAZARDOUS",
      definition: "Liquids with cadmium > 100 mg/l",
    },
    {
      value: "723",
      classification: "HAZARDOUS",
      definition: "Liquids with chromium (VI) > 500 mg/l",
    },
    {
      value: "724",
      classification: "HAZARDOUS",
      definition: "Liquids with lead > 500 mg/l",
    },
    {
      value: "725",
      classification: "HAZARDOUS",
      definition: "Liquids with mercury > 20 mg/l",
    },
    {
      value: "726",
      classification: "HAZARDOUS",
      definition: "Liquids with nickel > 134 mg/l",
    },
    {
      value: "727",
      classification: "HAZARDOUS",
      definition: "Liquids with selenium > 100 mg/l",
    },
    {
      value: "728",
      classification: "HAZARDOUS",
      definition: "Liquids with thallium > 130 mg/l",
    },
    {
      value: "731",
      classification: "HAZARDOUS",
      definition: "Liquids with polychlorinated biphenyls > 50 mg/l",
    },
    {
      value: "741",
      classification: "HAZARDOUS",
      definition: "Liquids with halogenated organic compounds > 1000 mg/l",
    },
    {
      value: "751",
      classification: "HAZARDOUS",
      definition: "Solids or sludge with halogenated organic comp. > 1000 mg/kg",
    },
    {
      value: "791",
      classification: "HAZARDOUS",
      definition: "Liquids with pH < 2",
    },
    {
      value: "792",
      classification: "HAZARDOUS",
      definition: "Liquids with pH < 2 with metals",
    },
    {
      value: "801",
      classification: "HAZARDOUS",
      definition: "Waste potentially containing dioxins",
    },
  ],
  // CO — Colorado — Non-RCRA Hazardous Waste.
  CO: [
    {
      value: "K901",
      classification: "HAZARDOUS",
      definition:
        "Military Munitions: Waste Chemical weapons using or containing any chemical compound identified in Appendix VIII of Part 261 as the basis for this listing. Residues resulting from treatment of HW Codes P909, P910, and P911",
    },
    {
      value: "K902",
      classification: "HAZARDOUS",
      definition:
        "Military Munitions: Any soil, water, debris, or containers contaminated through contact with waste chemical weapons listed as K901 or hazardous waste listed as P909, P910, or P911",
    },
    {
      value: "K903",
      classification: "HAZARDOUS",
      definition: "MILITARY MUNITIONS - hydrolysate",
    },
    {
      value: "P909",
      classification: "HAZARDOUS",
      definition: "Mustard, Mustard Agent, Mustard Gas, H, HD",
    },
    {
      value: "P910",
      classification: "HAZARDOUS",
      definition: "Mustard, Mustard Agent, Mustard Gas, HT, Mustard T",
    },
    { value: "P911", classification: "HAZARDOUS", definition: "GB, Sarin" },
  ],
  // CT — Connecticut — Non-RCRA Hazardous Waste.
  CT: [
    {
      value: "CR01",
      classification: "HAZARDOUS",
      definition: "Waste PCBs with concentrations greater than or equal to 50 ppm",
    },
    { value: "CR02", classification: "HAZARDOUS", definition: "Waste Oil" },
    {
      value: "CR03",
      classification: "HAZARDOUS",
      definition: "Wastewater soluble oil",
    },
    {
      value: "CR04",
      classification: "HAZARDOUS",
      definition:
        "Waste chemical liquids that are toxic, hazardous to handle, and/or may cause contamination of ground and/or surface water",
    },
    {
      value: "CR05",
      classification: "HAZARDOUS",
      definition: "Waste chemical solids and semi-solids",
    },
  ],
  // IL — Illinois — State-regulated waste, not subject to the federal manifest. Tagged HAZARDOUS so the engine derives STATE_REGULATED (a state code is never federal-haz); subjectToEManifest:false reflects the waste-team confirmation (2026-06-08) that Illinois codes are not e-Manifest-subject.
  IL: [
    {
      value: "IL01",
      classification: "HAZARDOUS",
      definition: "Leaking underground storage tank contaminated soil, sand, and clay",
      subjectToEManifest: false,
    },
    {
      value: "IL02",
      classification: "HAZARDOUS",
      definition: "other contaminated soil, sand, or clay",
      subjectToEManifest: false,
    },
    {
      value: "IL03",
      classification: "HAZARDOUS",
      definition: "other contaminated materials",
      subjectToEManifest: false,
    },
    {
      value: "IL04",
      classification: "HAZARDOUS",
      definition: "PCB Solids such as Capacitors, Transformer Carcasses",
      subjectToEManifest: false,
    },
    {
      value: "IL05",
      classification: "HAZARDOUS",
      definition: "PCB liquids such as transformer and capacitor oils",
      subjectToEManifest: false,
    },
    {
      value: "IL06",
      classification: "HAZARDOUS",
      definition: "lab packs",
      subjectToEManifest: false,
    },
    {
      value: "IL07",
      classification: "HAZARDOUS",
      definition: "leachate",
      subjectToEManifest: false,
    },
    {
      value: "IL08",
      classification: "HAZARDOUS",
      definition: "ashes, incinerator or boiler",
      subjectToEManifest: false,
    },
    {
      value: "IL09",
      classification: "HAZARDOUS",
      definition: "municipal WW treatment sludges",
      subjectToEManifest: false,
    },
    {
      value: "IL10",
      classification: "HAZARDOUS",
      definition: "Industrial WW treatment sludges",
      subjectToEManifest: false,
    },
    {
      value: "IL11",
      classification: "HAZARDOUS",
      definition: "Food processing waste, off-spec food products",
      subjectToEManifest: false,
    },
    {
      value: "IL12",
      classification: "HAZARDOUS",
      definition: "Antifreeze",
      subjectToEManifest: false,
    },
    {
      value: "IL13",
      classification: "HAZARDOUS",
      definition: "Waste/used oil",
      subjectToEManifest: false,
    },
    {
      value: "IL14",
      classification: "HAZARDOUS",
      definition: "Other organic liquids",
      subjectToEManifest: false,
    },
    {
      value: "IL15",
      classification: "HAZARDOUS",
      definition: "Other organic solids or sludges",
      subjectToEManifest: false,
    },
    {
      value: "IL16",
      classification: "HAZARDOUS",
      definition: "liquids with other metals",
      subjectToEManifest: false,
    },
    {
      value: "IL17",
      classification: "HAZARDOUS",
      definition: "solids or sludges with other metals",
      subjectToEManifest: false,
    },
    {
      value: "IL18",
      classification: "HAZARDOUS",
      definition: "other inorganic liquids",
      subjectToEManifest: false,
    },
    {
      value: "IL19",
      classification: "HAZARDOUS",
      definition: "other inorganic solids or sludges",
      subjectToEManifest: false,
    },
    {
      value: "IL20",
      classification: "HAZARDOUS",
      definition: "containerized gas",
      subjectToEManifest: false,
    },
    {
      value: "IL21",
      classification: "HAZARDOUS",
      definition: "household hazardous waste from collections",
      subjectToEManifest: false,
    },
  ],
  // IN — Indiana — Non-RCRA Acutely Hazardous Waste.
  IN: [
    {
      value: "I001",
      classification: "HAZARDOUS",
      definition: "VX (O-ethyl-S-(2-diisopropylaminoethyl) methyl phosphonothiolate)",
    },
  ],
  // KS — Kansas — Non-RCRA Hazardous Waste.
  KS: [
    {
      value: "EBOLA",
      classification: "HAZARDOUS",
      definition: "Waste contaminated with Ebola virus",
    },
  ],
  // KY — Kentucky — Non-RCRA Hazardous Waste.
  KY: [
    {
      value: "N001",
      classification: "HAZARDOUS",
      definition: "GB (isopropyl methyl phosphonofluoridate) and related compounds (H)",
    },
    {
      value: "N002",
      classification: "HAZARDOUS",
      definition:
        "VX (0-ethyl-S-(2-diisopropyl-aminoethyl)methyl phosphonothiolate) and related compounds (H)",
    },
    {
      value: "N003",
      classification: "HAZARDOUS",
      definition: "H (bis (2-chloroethyl) sulfide) and related compounds (H)",
    },
    {
      value: "N1001",
      classification: "HAZARDOUS",
      definition:
        "GB contaminated waste equipment, tools, and construction materials that have been decontaminated in accordance with United States Army Guidelines and have been determined to be safe for storage or transport, and approved by the Cabinet as no longer acutely hazardous",
    },
    {
      value: "N1002",
      classification: "HAZARDOUS",
      definition:
        "VX contaminated waste equipment, tools, and construction materials that have been decontaminated in accordance with United States Army Guidelines and have been determined to be safe for storage or transport, and approved by the Cabinet as no longer acutely hazardous",
    },
    {
      value: "N1003",
      classification: "HAZARDOUS",
      definition:
        "H contaminated waste equipment, tools, and construction materials that have been decontaminated in accordance with United States Army Guidelines and have been determined to be safe for storage or transport, and approved by the Cabinet as no longer acutely hazardous",
    },
    {
      value: "N101",
      classification: "HAZARDOUS",
      definition:
        "Uncontaminated M67 Rocket Motor Assembly, Propellant Component of the Rocket Motor, Shipping Firing Tubes, and End-Caps associated with GB munitions",
    },
    {
      value: "N102",
      classification: "HAZARDOUS",
      definition:
        "Uncontaminated M67 Rocket Motor Assembly, Propellant Component of the Rocket Motor, Shipping Firing Tubes, and End-Caps associated with VX munitions",
    },
    {
      value: "N201",
      classification: "HAZARDOUS",
      definition:
        "Metal Parts Treater Residue or Static Detonation Chamber Residues and Ash, and Post Thermal Oxidizer Solids and Sludges associated with GB munitions or related wastes",
    },
    {
      value: "N202",
      classification: "HAZARDOUS",
      definition:
        "Metal Parts Treater Residue or Static Detonation Chamber Residues and Ash, and Post Thermal Oxidizer Solids and Sludges associated with VX munitions or related wastes",
    },
    {
      value: "N203",
      classification: "HAZARDOUS",
      definition:
        "Static Detonation Chamber Residue and Ash, and Post Thermal Oxidizer Solids and Sludges associated with H munitions",
    },
    {
      value: "N301",
      classification: "HAZARDOUS",
      definition: "Agent Hydrolysate associated with GB munitions",
    },
    {
      value: "N302",
      classification: "HAZARDOUS",
      definition: "Agent Hydrolysate associated with VX munitions",
    },
    {
      value: "N401",
      classification: "HAZARDOUS",
      definition: "Energetic Hydrolysate associated with GB munitions",
    },
    {
      value: "N402",
      classification: "HAZARDOUS",
      definition: "Energetic Hydrolysate associated with VX munitions",
    },
    {
      value: "N501",
      classification: "HAZARDOUS",
      definition: "Aluminum Precipitate associated with treated GB wastes",
    },
    {
      value: "N502",
      classification: "HAZARDOUS",
      definition: "Aluminum Precipitate associated with treated VX wastes",
    },
    {
      value: "N601",
      classification: "HAZARDOUS",
      definition:
        "Reverse Osmosis Reject or Supercritical Water Oxidation Effluent associated with treated GB wastes",
    },
    {
      value: "N602",
      classification: "HAZARDOUS",
      definition:
        "Reverse Osmosis Reject or Supercritical Water Oxidation Effluent associated with treated VX wastes",
    },
    {
      value: "N701",
      classification: "HAZARDOUS",
      definition:
        "Lab Wastes associated with treated GB wastes and GB-containing lab wastes treated to destroy agent with caustic",
    },
    {
      value: "N702",
      classification: "HAZARDOUS",
      definition:
        "Lab Wastes associated with treated VX wastes and VX-containing lab wastes treated to destroy agent with caustic",
    },
    {
      value: "N703",
      classification: "HAZARDOUS",
      definition:
        "Lab Wastes associated with treated H wastes and H-containing lab wastes treated to destroy agent with caustic",
    },
    {
      value: "N801",
      classification: "HAZARDOUS",
      definition:
        "Off-gas Treatment (OTM) condensate associated with treated GB wastes, Off-gas Treatment System liquids including  Quench Fluid, Scrubber Fluids, Electrostatic Precipitator Fluids, Process Fluid Bleed Water, Post-Thermal Oxidizer Condensates and related wastes associated with Static Detonation Chamber treatment of GB munitions",
    },
    {
      value: "N802",
      classification: "HAZARDOUS",
      definition:
        "Off-gas Treatment (OTM) condensate associated with treated VX wastes, Off-gas Treatment System liquids including  Quench Fluid, Scrubber Fluids, Electrostatic Precipitator Fluids, Process Fluid Bleed Water, Post-Thermal Oxidizer Condensates and related wastes associated with 8 Static Detonation Chamber treatment of VX munitions",
    },
    {
      value: "N803",
      classification: "HAZARDOUS",
      definition:
        "Off-gas Treatment System liquids including Quench Fluid, Scrubber Fluids, Post-Thermal Oxidizer Condensates and related wastes associated with Static Detonation Chamber treatment of H munitions",
    },
    {
      value: "N901",
      classification: "HAZARDOUS",
      definition: "Spent Decontamination Solution associated with treated GB wastes",
    },
    {
      value: "N902",
      classification: "HAZARDOUS",
      definition: "Spent Decontamination Solution associated with treated VX wastes",
    },
    {
      value: "N903",
      classification: "HAZARDOUS",
      definition: "Spent Decontamination Solution associated with treated H wastes",
    },
  ],
  // MA — Massachusetts — Non-RCRA Hazardous Waste. MA99 reclassified to NONHAZARDOUS per Oct 2025 migration (ENG-15211).
  MA: [
    {
      value: "MA00",
      classification: "HAZARDOUS",
      definition: "Hazardous waste designated as such pursuant to 310 CMR 30.144.",
    },
    {
      value: "MA01",
      classification: "HAZARDOUS",
      definition:
        "Waste oil means used or unused waste oil (or any mixture thereof) that is not otherwise hazardous waste pursuant to 310 CMR 30.120 through 30.136, except that used waste oil that has a flash point greater than or equal to 100GF and less than 140GF (solely through use) remains subject to regulation as used waste oil.",
    },
    {
      value: "MA02",
      classification: "HAZARDOUS",
      definition:
        "Wastes which contain polychlorinated biphenyls (PCBs) in concentrations equal to or greater than 50 parts per million.",
    },
    {
      value: "MA04",
      classification: "HAZARDOUS",
      definition:
        "Waste generated in the manufacture of paint (e.g., oils, shellac, varnish, stains, lacquer, latex, enamel, alkyds, urethanes, acrylics, casein) which is not otherwise regulated as hazardous waste pursuant to 310 CMR 30.120 through 30.125 (characteristics of hazardous waste) or 310 CMR 30.130 through 30.136 (lists of hazardous wastes) if: (1) The paint is formulated with one or more ingredients which are listed as hazardous constituents in 310 CMR 30.160; or (2) The paint is formulated with any ingredient which contains 1% or more by weight of hazardous constituents listed in 310 CMR 30.160.",
    },
    {
      value: "MA95",
      classification: "HAZARDOUS",
      definition:
        "Universal waste shipped on a hazardous waste manifest by a licensed hazardous waste transporter.",
    },
    {
      value: "MA97",
      classification: "HAZARDOUS",
      definition:
        "Class A regulated recyclable material (including, but not limited to, specification used oil fuel) that is shipped using a hazardous waste manifest.",
    },
    {
      value: "MA98",
      classification: "HAZARDOUS",
      definition:
        "Off-specification used oil fuel that is shipped using a hazardous waste manifest.",
    },
    {
      value: "MA99",
      classification: "NONHAZARDOUS",
      definition:
        "Not hazardous waste. This designation is to be used only for material that is not hazardous waste and that is shipped using a hazardous waste manifest.",
      // EPA lists MA99 as state-only subject to RCRA manifest despite its NONHAZARDOUS classification.
      subjectToEManifest: true,
    },
  ],
  // MD — Maryland — Non-RCRA Hazardous Waste.
  MD: [
    {
      value: "F014",
      classification: "HAZARDOUS",
      definition:
        "Cyanidation wastewater treatment tailing pond sediment from mineral metals recovery",
    },
    {
      value: "F015",
      classification: "HAZARDOUS",
      definition: "Spent cyanide bath solutions from mineral metals recovery operations",
    },
    {
      value: "K991",
      classification: "HAZARDOUS",
      definition:
        "Waste ethyl dimethylamidocyanophosphate, also known by the common names GA and Tabun and the following alternate chemical names: Ethyl N, N-dimethylphosphoramidocyanidate / Dimethylamidoethoxyphosphoryl cyanide",
    },
    {
      value: "K992",
      classification: "HAZARDOUS",
      definition:
        "Waste isopropyl methanefluorophosphonate, also known by the common names GB and Sarin and the following alternate chemical names: Isopropyl methylphosphonofluoridate / Isopropyl ester of methylphosphonofluoridic acid",
    },
    {
      value: "K993",
      classification: "HAZARDOUS",
      definition:
        "Waste 3,3-dimethyl-n-but-2-yl methylphosphonofluoridate, also known by the common names GD and Soman and the following alternate chemical names: Pinacolyl methyl-phosphonofluoridate / 1, 2, 2-trimethyl, methylphosphono-fluoridate / Pinacoloxymethylphosphoryl fluoridate",
    },
    {
      value: "K994",
      classification: "HAZARDOUS",
      definition:
        "Waste O-ethyl S-(2-diisopropyl-aminoethyl), methylphosphonothioate also known by the common name VX",
    },
    {
      value: "K995",
      classification: "HAZARDOUS",
      definition:
        "Waste chlorovinylarsine dichloride, also known by the names L and Lewisite and the following alternate chemical names: Dichloro (2-chlorovinyl) arsine / 2-chlorovinyldichlorarsine",
    },
    {
      value: "K996",
      classification: "HAZARDOUS",
      definition: "Waste phenarsazine chloride, also known by the common name Adamsite",
    },
    {
      value: "K997",
      classification: "HAZARDOUS",
      definition:
        "Waste bis(2-chloroethyl) sulfide, also known by the common name sulfur mustard and HD",
    },
    {
      value: "K998",
      classification: "HAZARDOUS",
      definition:
        "Waste 2,2' -di(3-chloroethylthio) -diethyl ether, also known by the common name T and the following alternate chemical name: Bis-(2-chloroethylthioethyl) ether",
    },
    {
      value: "K999",
      classification: "HAZARDOUS",
      definition:
        "Waste military chemical warfare agents (chemical surety agents) having any substances K991 through K998 as their active or principal ingredient or ingredients, or mixture of K991 through K998 with any characteristic or listed hazardous waste",
    },
    {
      value: "M001",
      classification: "HAZARDOUS",
      definition: "Polychlorinated Biphenyls (greater than 500 ppm)",
    },
    {
      value: "MD01",
      classification: "HAZARDOUS",
      definition:
        "Filter cake and sludge from API separators, generated during the production of phthalate esters",
    },
    {
      value: "MD02",
      classification: "HAZARDOUS",
      definition:
        "Reaction products resulting from the decontamination of certain chemical warfare agents",
    },
    {
      value: "MD03",
      classification: "HAZARDOUS",
      definition:
        "Residuals from the treatment of wastes K991 through K999 or solid items that contained one or more of the wastes K991 through K999 and that have been decontaminated.",
    },
    {
      value: "MT01",
      classification: "HAZARDOUS",
      definition:
        "Polychlorinated Biphenyls (50 to 500 ppm) MX01Mixtures of soil, water, or debris containing PCBs with concentrations greater than 50 ppm or any residue resulting from the cleanup of a spill of any commercial chemical product or manufacturing product or manufacturing chemical intermediate having the generic name listed in COMAR 26.13.02.19E and G",
    },
    {
      value: "U202",
      classification: "HAZARDOUS",
      definition: "Saccharin and salts",
    },
    {
      value: "MX01",
      classification: "NONHAZARDOUS",
      definition:
        "Mixtures of soil, water, or debris containing PCBs with concentrations greater than 50 ppm or any residue resulting from the cleanup of a spill of any commercial chemical product or manufacturing product or manufacturing chemical intermediate having the generic name listed in COMAR 26.13.02.19E and G",
      // EPA lists MX01 as state-only subject to RCRA manifest; NONHAZ classification is parity-only carryover.
      subjectToEManifest: true,
    },
  ],
  // ME — Maine — Non-RCRA Hazardous Waste, including ME Ch. 850 K-codes, MED-prefix D-codes, and MR-prefix universal waste series.
  ME: [
    {
      value: "K119",
      classification: "HAZARDOUS",
      definition: "Wastes from the decantor in the production of linuron.",
    },
    {
      value: "K120",
      classification: "HAZARDOUS",
      definition: "Wastes from the spill control trap in production of linuron.",
    },
    {
      value: "K121",
      classification: "HAZARDOUS",
      definition:
        "Wastewater from product filtration and water washing in the production of bromacil.",
    },
    {
      value: "M002",
      classification: "HAZARDOUS",
      definition: "PCB and PCBs are identified as toxic wastes (T).",
    },
    {
      value: "M003",
      classification: "HAZARDOUS",
      definition:
        "Waste oils and coolant oils with greater than 4,000 ppm halogens due to chlorinated paraffins that are federally-exempt.",
    },
    {
      value: "MED001",
      classification: "HAZARDOUS",
      definition: "Regulated ignitable waste, otherwise federally exempt",
    },
    {
      value: "MED018",
      classification: "HAZARDOUS",
      definition: "Regulated waste with benzene characteristic, otherwise federally exempt",
    },
    {
      value: "MRD001",
      classification: "HAZARDOUS",
      definition: "universal waste ignitable",
    },
    {
      value: "MRD002",
      classification: "HAZARDOUS",
      definition: "universal waste corrosive",
    },
    {
      value: "MRD003",
      classification: "HAZARDOUS",
      definition: "universal waste reactive",
    },
    {
      value: "MRD006",
      classification: "HAZARDOUS",
      definition: "universal waste cadmium",
    },
    {
      value: "MRD007",
      classification: "HAZARDOUS",
      definition: "universal waste chromium",
    },
    {
      value: "MRD008",
      classification: "HAZARDOUS",
      definition: "universal waste lead",
    },
    {
      value: "MRD009",
      classification: "HAZARDOUS",
      definition: "universal waste mercury",
    },
    {
      value: "MRD011",
      classification: "HAZARDOUS",
      definition: "universal waste silver",
    },
    {
      value: "MRM002",
      classification: "HAZARDOUS",
      definition: "universal waste PCB",
    },
    {
      value: "P124",
      classification: "HAZARDOUS",
      definition: "Actinomycin D*",
    },
    {
      value: "P125",
      classification: "HAZARDOUS",
      definition: "Antimony, when in the form of particles 100 microns or less*",
    },
    {
      value: "P126",
      classification: "HAZARDOUS",
      definition: "Paraquat* / 4,4'-Bipyridinium, 1,1'-dimethyl, dichloride*",
    },
    { value: "P129", classification: "HAZARDOUS", definition: "Chlorine*" },
    { value: "P130", classification: "HAZARDOUS", definition: "Coumaphos*" },
    {
      value: "P131",
      classification: "HAZARDOUS",
      definition:
        "Mevinphos* / Coumarin,3-chloro-7 hydroxy-4-methyl,0-ester with 0,0-diethyl phosphorothioate*/ Crotonic acid, 3-hydroxy-, methyl ester, dimethyl phosphate (E)*",
    },
    {
      value: "P132",
      classification: "HAZARDOUS",
      definition: "Nitrogen mustard* / Diethylamine, 2,2'-dichloro-N-methyl-*",
    },
    {
      value: "P133",
      classification: "HAZARDOUS",
      definition: "Chloroethanol*",
    },
    {
      value: "P134",
      classification: "HAZARDOUS",
      definition:
        "Cycloheximide* / Glutarimide,3-(2-(3,5-dimethyl-2-oxocyclohexyl)-2 hydroxyethyl)*",
    },
    {
      value: "P135",
      classification: "HAZARDOUS",
      definition: "Phenytoin* / Hydantoin, 5,5-diphenyl-*",
    },
    {
      value: "P136",
      classification: "HAZARDOUS",
      definition: "Phenytoin sodium* / Hydantoin, 5,5-diphenyl-monosodium salt*",
    },
    { value: "P137", classification: "HAZARDOUS", definition: "Hydroquinone*" },
    {
      value: "P138",
      classification: "HAZARDOUS",
      definition: "Isonicotinic acid hydrazide*",
    },
    {
      value: "P139",
      classification: "HAZARDOUS",
      definition: "Phosphonic acid, (2,2,2-thrichloro-1, hydroxyethyl) -, dimethyl ester*",
    },
    {
      value: "P140",
      classification: "HAZARDOUS",
      definition:
        "Leptophos* / Phosphonothioic acid, phenyl-0-(4-bromo-2,5-dichlorophenyl) 0-methyl ester*",
    },
    {
      value: "P141",
      classification: "HAZARDOUS",
      definition: "EPN* / Phosphorothioic acid, phenyl-,0-ethyl 0-(p-nitrophenyl) ester*",
    },
    {
      value: "P142",
      classification: "HAZARDOUS",
      definition:
        "Phosacetim* / Phosphoramidiothioic acid, acetimidoyl-,0,0-bis(p-chlorophenyl) ester*",
    },
    {
      value: "P143",
      classification: "HAZARDOUS",
      definition:
        "Chlorofenvinphos* / Phosphoric acid, 2-chloro-1-(2,4-dichlorophenyl) vinyl diethyl ester*",
    },
    {
      value: "P144",
      classification: "HAZARDOUS",
      definition: "Dichlorvos* / Phosphoric acid, 2,2-dichlorovinyl dimethyl ester*",
    },
    {
      value: "P145",
      classification: "HAZARDOUS",
      definition:
        "Phosphoric acid, dimethyl ester, ester with 2-chloro-N,N-diethyl-3-hydroxycrotonamide*",
    },
    {
      value: "P146",
      classification: "HAZARDOUS",
      definition:
        "Dicrotophos* / Phosphoric acid, dimethyl ester, ester with (E)-3-hydroxy-N,N-dimethylcrotonamide*",
    },
    {
      value: "P147",
      classification: "HAZARDOUS",
      definition:
        "Monocrotophos* / Phosphoric acid, dimethyl ester, ester with (E)-3-hydroxy-N, methyl-crotonamide*",
    },
    {
      value: "P148",
      classification: "HAZARDOUS",
      definition: "Phosphorodithioic acid, S-(((p-chlorophenyl)thio)-methyl) 0,0-diethyl ester*",
    },
    {
      value: "P149",
      classification: "HAZARDOUS",
      definition:
        "Terbufos* / Phosphorodithioic acid, 0,0-diethyl-S-(((1,1-dimethylethyl)thio) methyl)ester*",
    },
    {
      value: "P150",
      classification: "HAZARDOUS",
      definition:
        "Azinphos ethyl* / Phosphorodithioic acid, 0,0-diethyl ester, S-ester with 3-(mercaptomethyl)-1,2,3-benzotriazin-4(3H)-one*",
    },
    {
      value: "P151",
      classification: "HAZARDOUS",
      definition:
        "Azinphos methyl* / Phosphorodithioic acid, 0,0-dimethyl ester, S-ester with 3-(mercaptomethyl)-1,2,3-benzotriazin-4(3H)-one*",
    },
    {
      value: "P152",
      classification: "HAZARDOUS",
      definition:
        "Phosmet* / Phosphorodithioic acid, 0,0-dimethyl ester, S-ester with N-(mercaptomethyl) phthalimide*",
    },
    {
      value: "P153",
      classification: "HAZARDOUS",
      definition:
        "Dioxathion* / Phosphorodithioic acid, S,S'-p-dioxane-2,3-diyl 0,0,0',0'-tetra-ethyl ester*",
    },
    {
      value: "P154",
      classification: "HAZARDOUS",
      definition: "Ethion* / Phosphorodithioic acid, S,S'-methylene 0,0,0',0'-tetraethyl ester**",
    },
    {
      value: "P155",
      classification: "HAZARDOUS",
      definition:
        "Demeton* / Phosphorothioic acid, 0,0-diethyl 0-(2-(ethylthio)ethyl) ester, mixed with 0-0-diethyl S-(2-(ethylthio)ethyl) ester 7:3)*",
    },
    {
      value: "P156",
      classification: "HAZARDOUS",
      definition:
        "Fensulfothion* / Phosphorothioic acid, 0,0-diethyl 0-(p-methyl sulfinyl)phenyl) ester*",
    },
    {
      value: "P157",
      classification: "HAZARDOUS",
      definition:
        "Oxydemeton-Methyl* / Phosphorothioic acid, S-(2-(ethyl-sulfinyl) ethyl)0,0-dimethyl ester*",
    },
    {
      value: "P158",
      classification: "HAZARDOUS",
      definition: "Sulfide, bis (2-chloro-ethyl)-*",
    },
    {
      value: "U139",
      classification: "HAZARDOUS",
      definition: "Ferric dextran",
    },
    {
      value: "U354",
      classification: "HAZARDOUS",
      definition: "Bromacil* / 5-Bromo-3-sec-butyl-6-methyluracil*",
    },
    {
      value: "U355",
      classification: "HAZARDOUS",
      definition: "Linuron* / N'(3,4-dichlorophenyl)-N-methoxy-N-methyl urea*",
    },
  ],
  // MI — Michigan — Non-RCRA Hazardous Waste.
  MI: [
    { value: "001S", classification: "HAZARDOUS", definition: "Aflatoxin" },
    {
      value: "001T",
      classification: "HAZARDOUS",
      definition: "Michigan waste code - Dow Chemical (MID000724724) incinerator ash",
    },
    {
      value: "001U",
      classification: "HAZARDOUS",
      definition: "Actinomycin D (Chemical Abstract Services Number 50-76-0)",
    },
    {
      value: "002S",
      classification: "HAZARDOUS",
      definition: "2,3,7,8-Tetrachlorodibenzo-p-dioxin",
    },
    {
      value: "002U",
      classification: "HAZARDOUS",
      definition: "Allyl chloride (Chemical Abstract Services Number 107-05-1)",
    },
    {
      value: "003S",
      classification: "HAZARDOUS",
      definition: "1,2,3,7,8-Pentachlorodibenzo-p-dioxin",
    },
    {
      value: "003T",
      classification: "HAZARDOUS",
      definition: "Michigan waste code - Dow Chemical (MID000724724) primary wastewater",
    },
    {
      value: "003U",
      classification: "HAZARDOUS",
      definition: "2-aminoanthraquinone (Chemical Abstract Services Number 117-79-3)",
    },
    {
      value: "004S",
      classification: "HAZARDOUS",
      definition: "1,2,3,4,7,8-Hexachlorodibenzo-p-dioxin",
    },
    {
      value: "004T",
      classification: "HAZARDOUS",
      definition:
        "Michigan waste code - Dow Chemical (MID000724724) deep well disposal spent filters",
    },
    {
      value: "004U",
      classification: "HAZARDOUS",
      definition: "Aminoazobenzene (Chemical Abstract Services Number 60-09-3)",
    },
    {
      value: "005S",
      classification: "HAZARDOUS",
      definition: "1,2,3,6,7,8-Hexachlorodibenzo-p-dioxin",
    },
    {
      value: "005T",
      classification: "HAZARDOUS",
      definition: "Michigan waste code - Dow Chemical (MID000724724) secondary wastewater",
    },
    {
      value: "005U",
      classification: "HAZARDOUS",
      definition: "0-aminoazotoluene (Chemical Abstract Services Number 97-56-3)",
    },
    {
      value: "006S",
      classification: "HAZARDOUS",
      definition: "1,2,3,7,8,9-Hexachlorodibenzo-p-dioxin",
    },
    {
      value: "006U",
      classification: "HAZARDOUS",
      definition: "4-aminobiphenyl (Chemical Abstract Services Number 92-67-1)",
    },
    {
      value: "007S",
      classification: "HAZARDOUS",
      definition: "2,3,7,8-Tetrachloridibenzo furan",
    },
    {
      value: "007U",
      classification: "HAZARDOUS",
      definition: "3-amino-9-ethyl carbazole (Chemical Abstract Services Number 132-32-1)",
    },
    {
      value: "008U",
      classification: "HAZARDOUS",
      definition: "1-amino-2-methyl anthraquinone (Chemical Abstract Services Number 82-28-0)",
    },
    {
      value: "009U",
      classification: "HAZARDOUS",
      definition: "Anilazine (Chemical Abstract Services Number 101-05-3)",
    },
    {
      value: "011U",
      classification: "HAZARDOUS",
      definition: "o-Anisidine (Chemical Abstract Services Number 90-04-0)",
    },
    {
      value: "012U",
      classification: "HAZARDOUS",
      definition: "o-Anisidine hydrochloride (Chemical Abstract Services Number 134-29-2)",
    },
    {
      value: "013U",
      classification: "HAZARDOUS",
      definition:
        "Antimony (when in the form of particles 100 microns or less) (Chemical Abstract Services Number Class-01-0)",
    },
    {
      value: "014U",
      classification: "HAZARDOUS",
      definition: "Antimycin A (Chemical Abstract Services Number 1397-94-0)",
    },
    {
      value: "015U",
      classification: "HAZARDOUS",
      definition: "Barban (Chemical Abstract Services Number 101-27-9)",
    },
    {
      value: "016U",
      classification: "HAZARDOUS",
      definition: "Bendiocarb (Chemical Abstract Services Number 22781-23-3)",
    },
    {
      value: "017U",
      classification: "HAZARDOUS",
      definition: "Benomyl (Chemical Abstract Services Number 17804-35-2)",
    },
    {
      value: "020U",
      classification: "HAZARDOUS",
      definition: "Bromoxynil (Chemical Abstract Services Number 1689-84-5)",
    },
    {
      value: "021U",
      classification: "HAZARDOUS",
      definition:
        "2(p-tert-Butylphenoxy)-isopropyl-2-chloroethyl sulfite (Chemical Abstract Services Number 140-57-8)",
    },
    {
      value: "022U",
      classification: "HAZARDOUS",
      definition: "Captafol (Chemical Abstract Services 2425-06-1)",
    },
    {
      value: "023U",
      classification: "HAZARDOUS",
      definition: "Captan (Chemical Abstract Services Number 133-06-2)",
    },
    {
      value: "024U",
      classification: "HAZARDOUS",
      definition: "Carbaryl (Chemical Abstract Services Number 63-25-3)",
    },
    {
      value: "025U",
      classification: "HAZARDOUS",
      definition: "Carbofuran (Chemical Abstract Services Number 1563-66-2)",
    },
    {
      value: "027U",
      classification: "HAZARDOUS",
      definition: "Carbophenothion (Chemical Abstract Services Number 786-19-6)",
    },
    {
      value: "028U",
      classification: "HAZARDOUS",
      definition: "Chloramines (Chemical Abstract Services Number Class-08-6)",
    },
    {
      value: "029U",
      classification: "HAZARDOUS",
      definition: "Chloropyrifos (Chemical Abstract Services Number 2921-88-2)",
    },
    {
      value: "030U",
      classification: "HAZARDOUS",
      definition:
        "Chlorinated dibenzofurans (other than those listed in Table 202) (Chemical Abstract Services Number Class-05-3)",
    },
    {
      value: "031U",
      classification: "HAZARDOUS",
      definition:
        "Chlorinated dioxins (other than those listed in Table 202) (Chemical Abstract Services Number Class-05-4)",
    },
    {
      value: "032U",
      classification: "HAZARDOUS",
      definition: "Chlorine gas (Chemical Abstract Services Number 7782-50-5)",
    },
    {
      value: "033U",
      classification: "HAZARDOUS",
      definition: "2-Chloroethanol (Chemical Abstract Services Number 107-07-3)",
    },
    {
      value: "034U",
      classification: "HAZARDOUS",
      definition:
        "3-(Chloromethyl) pyridine hydrochloride (Chemical Abstract Services Number 6959-48-4)",
    },
    {
      value: "036U",
      classification: "HAZARDOUS",
      definition: "4-chloro-m-phenylenediamine (Chemical Abstract Services Number 5131-60-2)",
    },
    {
      value: "037U",
      classification: "HAZARDOUS",
      definition: "4-chloro-o-phenylenediamine (Chemical Abstract Services Number 95-83-0)",
    },
    {
      value: "038U",
      classification: "HAZARDOUS",
      definition: "Chloroprene (Chemical Abstract Services Number 126-99-8)",
    },
    {
      value: "040U",
      classification: "HAZARDOUS",
      definition: "Clonitralid (Chemical Abstract Services Number 1420-04-8)",
    },
    {
      value: "041U",
      classification: "HAZARDOUS",
      definition:
        "Cobalt (when in the form of particles 100 microns or less) (Chemical Abstract Services Number Class-01-6)",
    },
    {
      value: "042U",
      classification: "HAZARDOUS",
      definition: "Coumasphos (Chemical Abstract Services Number 56-72-4)",
    },
    {
      value: "043U",
      classification: "HAZARDOUS",
      definition: "p-Cresidine (Chemical Abstract Services Number 120-71-8)",
    },
    {
      value: "044U",
      classification: "HAZARDOUS",
      definition: "Crotoxyphos (Chemical Abstract Services Number 7700-17-6)",
    },
    {
      value: "046U",
      classification: "HAZARDOUS",
      definition: "Cycloheximide (Chemical Abstract Services Number 66-81-9)",
    },
    {
      value: "047U",
      classification: "HAZARDOUS",
      definition: "Demeton (Chemical Abstract Services Number 8065-48-3)",
    },
    {
      value: "049U",
      classification: "HAZARDOUS",
      definition: "4,4'-Diaminodiphenyl ether (Chemical Abstract Services Number 101-80-4)",
    },
    {
      value: "050U",
      classification: "HAZARDOUS",
      definition: "2,4-Diaminotoluene (Chemical Abstract Services Number 95-80-7)",
    },
    {
      value: "051U",
      classification: "HAZARDOUS",
      definition: "Diazinon (Chemical Abstract Services Number 333-41-5)",
    },
    {
      value: "052U",
      classification: "HAZARDOUS",
      definition: "Dichlone (Chemical Abstract Services Number 117-80-6)",
    },
    {
      value: "054U",
      classification: "HAZARDOUS",
      definition: "Dichlorvos (Chemical Abstract Services Number 62-73-7)",
    },
    {
      value: "055U",
      classification: "HAZARDOUS",
      definition: "Dichrotophos (Chemical Abstract Services Number 141-66-2)",
    },
    {
      value: "056U",
      classification: "HAZARDOUS",
      definition: "Diethyl sulfate (Chemical Abstract Services Number 64-67-5)",
    },
    {
      value: "057U",
      classification: "HAZARDOUS",
      definition: "Dinocap (Chemical Abstract Services Number 39300-45-3)",
    },
    {
      value: "058U",
      classification: "HAZARDOUS",
      definition: "Dioxathion (Chemical Abstract Services Number 78-34-2)",
    },
    {
      value: "059U",
      classification: "HAZARDOUS",
      definition: "EPN (Chemical Abstract Services Number 2104-64-5)",
    },
    {
      value: "061U",
      classification: "HAZARDOUS",
      definition: "Ethion (Chemical Abstract Services Number 563-12-2)",
    },
    {
      value: "063U",
      classification: "HAZARDOUS",
      definition: "Fensulfothion (Chemical Abstract Services Number 115-90-2)",
    },
    {
      value: "064U",
      classification: "HAZARDOUS",
      definition: "Fenthion (Chemical Abstract Services Number 55-38-9)",
    },
    {
      value: "065U",
      classification: "HAZARDOUS",
      definition: "Fluchloralin (Chemical Abstract Services Number 33245-39-5)",
    },
    {
      value: "068U",
      classification: "HAZARDOUS",
      definition: "Hexamethyl phosphoramide (Chemical Abstract Services Number 680-31-9)",
    },
    {
      value: "070U",
      classification: "HAZARDOUS",
      definition: "Hydroquinone (Chemical Abstract Services Number 123-31-9)",
    },
    {
      value: "071U",
      classification: "HAZARDOUS",
      definition: "N-(2-Hydroxyethyl) ethyleneimine (Chemical Abstract Services Number 1072-52-2)",
    },
    {
      value: "072U",
      classification: "HAZARDOUS",
      definition: "Hypochlorite (Chemical Abstract Services Number 7778-54-3)",
    },
    {
      value: "073U",
      classification: "HAZARDOUS",
      definition: "Isonicotinic acid hydrazine (Chemical Abstract Services Number 54-85-3)",
    },
    {
      value: "074U",
      classification: "HAZARDOUS",
      definition: "Ketene (Chemical Abstract Services Number 463-51-4)",
    },
    {
      value: "075U",
      classification: "HAZARDOUS",
      definition: "Lactonitril (Chemical Abstract Services Number 78-97-7)",
    },
    {
      value: "076U",
      classification: "HAZARDOUS",
      definition: "Leptophos (Chemical Abstract Services Number 21609-90-5)",
    },
    {
      value: "078U",
      classification: "HAZARDOUS",
      definition: "Malachite green (Chemical Abstract Services Number 569-64-2)",
    },
    {
      value: "079U",
      classification: "HAZARDOUS",
      definition: "Malathion (Chemical Abstract Services Number 121-75-5)",
    },
    {
      value: "080U",
      classification: "HAZARDOUS",
      definition: "Mestranol (Chemical Abstract Services Number 72-33-3)",
    },
    {
      value: "082U",
      classification: "HAZARDOUS",
      definition: "4,4'-Methylenebis(2-methylaniline) (Chemical Abstract Services Number 838-88-0)",
    },
    {
      value: "083U",
      classification: "HAZARDOUS",
      definition:
        "4,4'-Methylenebis(N, N-dimethylaniline) (Chemical Abstract Services Number 101-61-1)",
    },
    {
      value: "086U",
      classification: "HAZARDOUS",
      definition: "1-Methylnaphthalene (Chemical Abstract Services Number 90-12-0)",
    },
    {
      value: "088U",
      classification: "HAZARDOUS",
      definition: "Mevinphos (Chemical Abstract Services Number 7786-34-7)",
    },
    {
      value: "092U",
      classification: "HAZARDOUS",
      definition: "Monocrotophos (Chemical Abstract Services Number 6923-22-4)",
    },
    {
      value: "093U",
      classification: "HAZARDOUS",
      definition: "Mustard gas (Chemical Abstract Services Number 505-60-2)",
    },
    {
      value: "094U",
      classification: "HAZARDOUS",
      definition: "Naled (Chemical Abstract Services Number 300-76-5)",
    },
    {
      value: "095U",
      classification: "HAZARDOUS",
      definition: "1,5-Napthalenediamine (Chemical Abstract Services Number 2243-62-1)",
    },
    {
      value: "096U",
      classification: "HAZARDOUS",
      definition:
        "Nickel (when in the form of particles 100 microns or less) (Chemical Abstract Services Number Class-02-2)",
    },
    {
      value: "097U",
      classification: "HAZARDOUS",
      definition: "Niridazole (Chemical Abstract Services Number 61-57-4)",
    },
    {
      value: "098U",
      classification: "HAZARDOUS",
      definition: "Nithiazide (Chemical Abstract Services Number 139-94-6)",
    },
    {
      value: "099U",
      classification: "HAZARDOUS",
      definition: "5-Nitroacenaphthene (Chemical Abstract Services Number 602-87-9)",
    },
    {
      value: "100U",
      classification: "HAZARDOUS",
      definition: "Nitro-o-anisidine (Chemical Abstract Services Number 99-59-2)",
    },
    {
      value: "101U",
      classification: "HAZARDOUS",
      definition: "Nitrobiphenyl (Chemical Abstract Services Number 92-93-3)",
    },
    {
      value: "102U",
      classification: "HAZARDOUS",
      definition: "Nitrofen (Chemical Abstract Services Number 1836-75-5)",
    },
    {
      value: "103U",
      classification: "HAZARDOUS",
      definition:
        "N-(4-(5-nitro-2-furanyl)-2-thiazolyl)-acetamide (Chemical Abstract Services Number 531-82-8)",
    },
    {
      value: "104U",
      classification: "HAZARDOUS",
      definition: "Nitrogen mustard (Chemical Abstract Services Number 51-75-2)",
    },
    {
      value: "106U",
      classification: "HAZARDOUS",
      definition: "p-Nitrosodiphenylamine (Chemical Abstract Services Number 156-10-5)",
    },
    {
      value: "108U",
      classification: "HAZARDOUS",
      definition:
        "N-nitroso-N-phenylhydroxylamine, ammonium salt (Chemical Abstract Services Number 135-20-6)",
    },
    {
      value: "110U",
      classification: "HAZARDOUS",
      definition: "Oxydemeton-methyl (Chemical Abstract Services Number 301-12-2)",
    },
    {
      value: "111U",
      classification: "HAZARDOUS",
      definition: "Paraquat (Chemical Abstract Services Number 1910-42-5)",
    },
    {
      value: "112U",
      classification: "HAZARDOUS",
      definition: "Peroxyacetic acid (Chemical Abstract Services Number 79-21-0)",
    },
    {
      value: "113U",
      classification: "HAZARDOUS",
      definition: "Phenazopyridine hydrochloride (Chemical Abstract Services Number 136-40-3)",
    },
    {
      value: "114U",
      classification: "HAZARDOUS",
      definition: "Phenesterin (Chemical Abstract Services Number 3546-10-9)",
    },
    {
      value: "115U",
      classification: "HAZARDOUS",
      definition: "Phenobarbitol (Chemical Abstract Services Number 50-06-6)",
    },
    {
      value: "116U",
      classification: "HAZARDOUS",
      definition: "Phenytoin (Chemical Abstract Services Number 57-41-0)",
    },
    {
      value: "117U",
      classification: "HAZARDOUS",
      definition: "Phenytoin sodium (Chemical Abstract Services Number 630-93-3)",
    },
    {
      value: "118U",
      classification: "HAZARDOUS",
      definition: "Phosazetim (Chemical Abstract Services Number 4104-14-7)",
    },
    {
      value: "119U",
      classification: "HAZARDOUS",
      definition: "Phosmet (Chemical Abstract Services Number 732-11-6)",
    },
    {
      value: "120U",
      classification: "HAZARDOUS",
      definition: "Phosphamidon (Chemical Abstract Services Number 13171-21-6)",
    },
    {
      value: "121U",
      classification: "HAZARDOUS",
      definition: "Piperonyl sulfoxide (Chemical Abstract Services Number 120-62-7)",
    },
    {
      value: "122U",
      classification: "HAZARDOUS",
      definition: "Polybrominated biphenyls (PBB) (Chemical Abstract Services Number Class-07-8)",
    },
    {
      value: "124U",
      classification: "HAZARDOUS",
      definition: "Propiolactone (Chemical Abstract Services Number 57-57-8)",
    },
    {
      value: "127U",
      classification: "HAZARDOUS",
      definition: "Propylthiouracil (Chemical Abstract Services Number 51-52-5)",
    },
    {
      value: "128U",
      classification: "HAZARDOUS",
      definition: "Rotenone (Chemical Abstract Services Number 83-74-4)",
    },
    {
      value: "129U",
      classification: "HAZARDOUS",
      definition: "Semicarbazide (Chemical Abstract Services Number 57-56-7)",
    },
    {
      value: "131U",
      classification: "HAZARDOUS",
      definition: "Styrene (Chemical Abstract Services Number 100-42-5)",
    },
    {
      value: "132U",
      classification: "HAZARDOUS",
      definition: "Sulfallate (Chemical Abstract Services Number 95-06-7)",
    },
    {
      value: "135U",
      classification: "HAZARDOUS",
      definition: "TEPP (Chemical Abstract Services Number 107-49-3)",
    },
    {
      value: "136U",
      classification: "HAZARDOUS",
      definition: "Terbufos (Chemical Abstract Services Number 13071-79-9)",
    },
    {
      value: "137U",
      classification: "HAZARDOUS",
      definition: "Tetrachlorvinphos (Chemical Abstract Services Number 961-11-5)",
    },
    {
      value: "138U",
      classification: "HAZARDOUS",
      definition: "4,4'-Thiodianiline (Chemical Abstract Services Number 139-65-1)",
    },
    {
      value: "140U",
      classification: "HAZARDOUS",
      definition: "Triaryl phosphate esters (Chemical Abstract Services Number Class-08-4)",
    },
    {
      value: "141U",
      classification: "HAZARDOUS",
      definition: "Trichlorfon (Chemical Abstract Services Number 52-68-6)",
    },
    {
      value: "142U",
      classification: "HAZARDOUS",
      definition: "Trifluralin (Chemical Abstract Services Number 1582-09-8)",
    },
    {
      value: "143U",
      classification: "HAZARDOUS",
      definition: "2,4,+B25:B1265-Trimethylaniline (Chemical Abstract Services Number 137-17-7)",
    },
    {
      value: "144U",
      classification: "HAZARDOUS",
      definition: "Trimethylphosphate (Chemical Abstract Services Number 512-56-1)",
    },
    {
      value: "146U",
      classification: "HAZARDOUS",
      definition: "Ziram (Chemical Abstract Services Number 137-30-4)",
    },
    {
      value: "147U",
      classification: "HAZARDOUS",
      definition: "Azinphos-ethyl (Chemical Abstract Services Number 2642-71-9)",
    },
    {
      value: "148U",
      classification: "HAZARDOUS",
      definition: "Azinphos-methyl (Chemical Abstract Services Number 86-50-0)",
    },
    {
      value: "150U",
      classification: "HAZARDOUS",
      definition: "2,4-Diaminotoluene (Chemical Abstract Services Number 95-80-7)",
    },
    {
      value: "151U",
      classification: "HAZARDOUS",
      definition: "Diazinon (Chemical Abstract Services Number 333-41-5)",
    },
    {
      value: "152U",
      classification: "HAZARDOUS",
      definition: "Dichlone (Chemical Abstract Services Number 117-80-6)",
    },
    {
      value: "154U",
      classification: "HAZARDOUS",
      definition: "Bis(tri-n-butyl tin) oxide (Chemical Abstract Services Number 56-35-9)",
    },
    {
      value: "155U",
      classification: "HAZARDOUS",
      definition: "Vinylidene chloride (Chemical Abstract Services Number 75-35-4)",
    },
    {
      value: "157U",
      classification: "HAZARDOUS",
      definition:
        "3-amino-9-ethyl carbazole hydrochloride (Chemical Abstract Services Number 57360-17-5)",
    },
    {
      value: "158U",
      classification: "HAZARDOUS",
      definition: "Aniline hydrochloride (Chemical Abstract Services Number 142-04-1)",
    },
    {
      value: "159U",
      classification: "HAZARDOUS",
      definition: "Azobenzene (Chemical Abstract Services Number 103-33-3)",
    },
    {
      value: "160U",
      classification: "HAZARDOUS",
      definition: "1,3-Butadiene (Chemical Abstract Services Number 106-99-0)",
    },
    {
      value: "161U",
      classification: "HAZARDOUS",
      definition: "Butyl benzyl phthalate (Chemical Abstract Services Number 85-68-7)",
    },
    {
      value: "162U",
      classification: "HAZARDOUS",
      definition: "1-chloro-4-phenoxybenzene (Chemical Abstract Services Number 7005-72-3)",
    },
    {
      value: "163U",
      classification: "HAZARDOUS",
      definition: "1-chloropropene (Chemical Abstract Services Number 590-21-6)",
    },
    {
      value: "164U",
      classification: "HAZARDOUS",
      definition: "P,P' DDE (Chemical Abstract Services Number 72-55-9)",
    },
    {
      value: "165U",
      classification: "HAZARDOUS",
      definition: "N,N'-Diethylthiourea (Chemical Abstract Services Number 105-55-5)",
    },
    {
      value: "166U",
      classification: "HAZARDOUS",
      definition: "1,2-Epoxybutane (Chemical Abstract Services Number 106-88-7)",
    },
    {
      value: "167U",
      classification: "HAZARDOUS",
      definition: "Kanechlor C (Chemical Abstract Services Number 59299-51-3)",
    },
    {
      value: "168U",
      classification: "HAZARDOUS",
      definition: "N-Nitrosomethylvinylamine (Chemical Abstract Services Number 4549-40-0)",
    },
    {
      value: "169U",
      classification: "HAZARDOUS",
      definition: "Octachlorostyrene (Chemical Abstract Services Number 29082-74-4)",
    },
    {
      value: "170U",
      classification: "HAZARDOUS",
      definition: "Semicarbazide hydrochloride (Chemical Abstract Services Number 563-41-7)",
    },
    {
      value: "171U",
      classification: "HAZARDOUS",
      definition:
        "Tributyltin (and other salts and esters) (Chemical Abstract Services Number 688-73-3)",
    },
    {
      value: "172U",
      classification: "HAZARDOUS",
      definition: "1,2,3-Trichlorobenzene (Chemical Abstract Services Number 87-61-6)",
    },
    {
      value: "173U",
      classification: "HAZARDOUS",
      definition: "1,2,4-Trichlorobenzene (Chemical Abstract Services Number 120-82-1)",
    },
    {
      value: "175U",
      classification: "HAZARDOUS",
      definition: "Vinyl bromide (Chemical Abstract Services Number 593-60-2)",
    },
    {
      value: "PCB1",
      classification: "HAZARDOUS",
      definition: "state defined - polychlorinated biphenyls bulk",
    },
    {
      value: "PCB3",
      classification: "HAZARDOUS",
      definition: "state defined - polychlorinated biphenyls from high voltage capacitors",
    },
    {
      value: "PCB4",
      classification: "HAZARDOUS",
      definition: "state defined - polychlorinated biphenyls from low voltage capacitors",
    },
    {
      value: "PCB5",
      classification: "HAZARDOUS",
      definition: "state defined - polychlorinated biphenyls article container",
    },
    {
      value: "PCB6",
      classification: "HAZARDOUS",
      definition: "state defined - polychlorinated biphenyls container",
    },
  ],
  // MN — Minnesota — Non-RCRA Hazardous Waste.
  MN: [
    { value: "MN01", classification: "HAZARDOUS", definition: "Lethal Waste" },
    {
      value: "MN02",
      classification: "HAZARDOUS",
      definition: "Lab Pack Waste",
    },
    {
      value: "MN03",
      classification: "HAZARDOUS",
      definition: "Toxic organic compounds such as pesticides and solvents.",
    },
    {
      value: "MN04",
      classification: "HAZARDOUS",
      definition: "Used oil managed as hazardous waste",
    },
  ],
  // NH — New Hampshire — Non-RCRA Hazardous Waste.
  NH: [
    { value: "NH01", classification: "HAZARDOUS", definition: "Used Oil" },
    {
      value: "NH02",
      classification: "HAZARDOUS",
      definition: "Solid Corrosives",
    },
    {
      value: "NHX1",
      classification: "HAZARDOUS",
      definition: "Recycling-Exempt",
    },
    {
      value: "NHX2",
      classification: "HAZARDOUS",
      definition: "Household Hazardous Waste Exempt",
    },
    {
      value: "NHX3",
      classification: "HAZARDOUS",
      definition: "Remediation Exempt",
    },
    { value: "NHX4", classification: "HAZARDOUS", definition: "Sludge Exempt" },
    {
      value: "NHX5",
      classification: "HAZARDOUS",
      definition: "Municipal Solid Waste Ash Exempt",
    },
    {
      value: "NHX6",
      classification: "HAZARDOUS",
      definition: "Shooting Range (Outdoor) Exempt",
    },
    {
      value: "NH03",
      classification: "NONHAZARDOUS",
      definition: "(TBD — definition not in release DB; flagged for ENG-15212 EPA reconciliation)",
      // Behavior-preservation override pending ENG-15502 follow-up; EPA does not list this code (see investigation doc).
      subjectToEManifest: true,
    },
  ],
  // NJ — New Jersey — State-tracked non-hazardous waste, not subject to the federal manifest (EPA does not list New Jersey state-only codes).
  NJ: [
    {
      value: "ID27",
      classification: "NONHAZARDOUS",
      definition: "Dry Industrial Solid Waste; exempt as per 40 CFR 261.4",
    },
    {
      value: "ID72",
      classification: "NONHAZARDOUS",
      definition: "Bulk liquid and semi-liquids waste; exempt as per 40 CFR 261.4",
    },
  ],
  // NV — Nevada — Non-Hazardous Waste Subject to RCRA Manifest.
  NV: [
    {
      value: "CAONLY",
      classification: "NONHAZARDOUS",
      definition: "Generic California State Waste Code",
      // EPA lists CAONLY as state-only subject to RCRA manifest.
      subjectToEManifest: true,
    },
    {
      value: "PCBX",
      classification: "NONHAZARDOUS",
      definition: "GENERIC PCB WASTE",
      // EPA lists PCBX as state-only subject to RCRA manifest.
      subjectToEManifest: true,
    },
  ],
  // NY — New York — Non-RCRA Hazardous Waste. The four single-letter codes (B/L/R/T) carry release-DB definitions that read as management-method codes (Incineration / Landfill / Material Recovery / Treatment) rather than NY state waste codes — ENG-15212 owns the EPA-list reconciliation that determines whether they belong here as-is, get a corrected definition, or get removed.
  NY: [
    {
      value: "B",
      classification: "HAZARDOUS",
      definition:
        "Incineration, heat recovery, burning. Waste management method code to be used on manifests only",
    },
    {
      value: "B001",
      classification: "HAZARDOUS",
      definition: "PCB oil (concentrated) from transformers, capacitors, etc.",
    },
    {
      value: "B002",
      classification: "HAZARDOUS",
      definition:
        "Petroleum oil or other liquid containing 50 ppm or greater of PCBs, but less than 500 ppm PCBs. This includes oil from electrical equipment whose PCB concentration is unknown, except for circuit breakers, re-closers, rectifiers, and cable.",
    },
    {
      value: "B003",
      classification: "HAZARDOUS",
      definition: "Petroleum oil or other liquid containing 500 ppm or greater of PCBs.",
    },
    {
      value: "B004",
      classification: "HAZARDOUS",
      definition:
        "PCB articles containing 50 ppm or greater of PCBs, but less than 500 ppm PCBs, excluding small capacitors. This includes oil-filled electrical equipment whose PCB concentration is unknown, except for circuit breakers, re-closers, rectifiers, and cable.",
    },
    {
      value: "B005",
      classification: "HAZARDOUS",
      definition:
        "PCB articles, other than transformers, that contain 500 ppm or greater of PCBs, excluding small capacitors.",
    },
    {
      value: "B006",
      classification: "HAZARDOUS",
      definition:
        "PCB transformers. PCB transformers means any transformer that contains 500 ppm PCB or greater.",
    },
    {
      value: "B007",
      classification: "HAZARDOUS",
      definition:
        "Other PCB wastes, including contaminated soil, solids, sludges, clothing, rags and dredge material.",
    },
    {
      value: "L",
      classification: "HAZARDOUS",
      definition: "Landfill. Waste management method code to be used on manifests only",
    },
    {
      value: "R",
      classification: "HAZARDOUS",
      definition:
        "Material Recovery of more than 75 percent of the total material. Waste management method code to be used on manifests only",
    },
    {
      value: "T",
      classification: "HAZARDOUS",
      definition:
        "Chemical, physical or biological treatment. Waste management method code to be used on manifests only",
    },
  ],
  // OR — Oregon — mixed (Non-Haz Manifest-Subject + Non-RCRA Haz + Non-RCRA Acutely Haz). Oregon also accepts the dynamic pattern `OR[UP]\d{3}` (federal U/P code prefixed with `OR`); those codes are validated by regex in `isValidStateWasteCode` and classified via the federal-code classifier, not enumerated here.
  OR: [
    {
      value: "F998",
      classification: "HAZARDOUS",
      definition:
        "Residues from demilitarization, treatment and testing of blister agents (such as mustard agents), which have been added to and made a part of the list found in 40 CFR 261.31.",
    },
    {
      value: "F999",
      classification: "HAZARDOUS",
      definition:
        "Residues from demilitarization, treatment and testing of nerve agents, such as GB (Sarin) and VX; which have been added to and made a part of the list found in 40 CFR 261.31.",
    },
    {
      value: "P998",
      classification: "HAZARDOUS",
      definition:
        "Blister agents, such as mustard agents, which have been added to the list of commercial chemical products found in 40 CFR 261.33(e).",
    },
    {
      value: "P999",
      classification: "HAZARDOUS",
      definition:
        "Nerve agents, such as GB (Sarin) and VX, which have been added to the list of commercial chemical products found in 40 CFR 261.33(e).",
    },
    {
      value: "X001",
      classification: "HAZARDOUS",
      definition:
        "Waste Pesticide residue, except for those that are managed as universal wastes; or whose constituents are listed in 40 CFR 261.24 (a), but are below the prescribed regulatory levels.",
    },
    {
      value: "X002",
      classification: "NONHAZARDOUS",
      definition: "Polychlorinated Biphenyls PCBs)",
      // EPA lists ORX002 as state-only subject to RCRA manifest; NONHAZ classification is from canonical.
      subjectToEManifest: true,
    },
    {
      value: "X004",
      classification: "NONHAZARDOUS",
      definition: "Non-hazardous industrial waste",
    },
    {
      value: "X006",
      classification: "NONHAZARDOUS",
      definition: "Lab Pack that is not considered hazardous waste under RCRA",
    },
    {
      value: "X007",
      classification: "HAZARDOUS",
      definition:
        "Wastes received from out of state that are not regulated in Oregon but are regulated as hazardous waste in state of origin.",
    },
    {
      value: "X008",
      classification: "NONHAZARDOUS",
      definition: "Household hazardous waste",
    },
  ],
  // RI — Rhode Island — Non-RCRA Hazardous Waste.
  RI: [
    { value: "R001", classification: "HAZARDOUS", definition: "TOXIC WASTE" },
    {
      value: "R006",
      classification: "HAZARDOUS",
      definition: "EXTREMELY HAZARDOUS WASTE",
    },
    { value: "R007", classification: "HAZARDOUS", definition: "PCB WASTE" },
    {
      value: "R009",
      classification: "HAZARDOUS",
      definition: "Mercury containing waste",
    },
    { value: "R010", classification: "HAZARDOUS", definition: "WASTE OIL" },
    {
      value: "R011",
      classification: "HAZARDOUS",
      definition:
        "Secondary Wastes generated by a HW Management facility as a result of treatment, repackaging, or storage.",
    },
    { value: "R012", classification: "HAZARDOUS", definition: "PMBHW" },
    { value: "R013", classification: "HAZARDOUS", definition: "Household HW" },
    {
      value: "R014",
      classification: "HAZARDOUS",
      definition: "Used oil sent for recycling",
    },
    {
      value: "R015",
      classification: "HAZARDOUS",
      definition: "Non-hazardous waste, manifest required by receiving state",
    },
  ],
  // SC — South Carolina — Non-RCRA Hazardous Waste.
  SC: [
    {
      value: "7777",
      classification: "HAZARDOUS",
      definition: "Non-hazardous waste received by a hazardous waste facility.",
    },
    {
      value: "K900",
      classification: "HAZARDOUS",
      definition:
        "Waste residues from the manufacture of organotin compounds which contain tri-(organo) substituted tin compounds, to include tributyltin and its analogs.",
    },
  ],
  // UT — Utah — Non-RCRA Hazardous Waste.
  UT: [
    {
      value: "F999",
      classification: "HAZARDOUS",
      definition:
        "Residues from demilitarization, treatment, and testing of nerve, military, and chemical agents CX, GA, GB, GD, H, HD, HL, HN-1, HN-2, HN-3, HT, L, T, and VX.",
    },
    {
      value: "P999",
      classification: "HAZARDOUS",
      definition:
        "Nerve, Military, and Chemical Agents (i.e., CX, GA, GB, GD, H, HD, HL, HN-1, HN-2, HN-3, HT, L, T, and VX.)",
    },
  ],
  // VA — Virginia — Non-RCRA Hazardous Waste.
  VA: [
    {
      value: "BCRUSH",
      classification: "HAZARDOUS",
      definition: "waste code for lamp/bulb crushers",
    },
  ],
  // VT — Vermont — Non-RCRA Hazardous Waste.
  VT: [
    {
      value: "VT01",
      classification: "HAZARDOUS",
      definition: "PCB 50ppm or greater",
    },
    {
      value: "VT02",
      classification: "HAZARDOUS",
      definition: "Petroleum distillates 5% or more",
    },
    {
      value: "VT03",
      classification: "HAZARDOUS",
      definition: "Water-miscible metal cutting fluid",
    },
    {
      value: "VT06",
      classification: "HAZARDOUS",
      definition: "Pesticide wastes",
    },
    {
      value: "VT08",
      classification: "HAZARDOUS",
      definition: "Ethylene glycol 700ppm or greater",
    },
    {
      value: "VT11",
      classification: "HAZARDOUS",
      definition: "Hazardous per 7-216",
    },
    {
      value: "VT20",
      classification: "HAZARDOUS",
      definition: "Solid becoming corrosive when mixed with water",
    },
    {
      value: "VT21",
      classification: "HAZARDOUS",
      definition: "Liquid wastes containing perfluorooctanoic acid (PFOA)",
    },
    {
      value: "VT22",
      classification: "HAZARDOUS",
      definition: "Liquid wastes containing perfluorooctanesulfonic acid (PFOS)",
    },
  ],
  // WA — Washington — Non-RCRA Hazardous Waste + Non-RCRA Acutely Hazardous Waste.
  WA: [
    {
      value: "WP01",
      classification: "ACUTELY_HAZARDOUS",
      definition: "State persistence criteria - halogenated organic compounds",
    },
    {
      value: "WP02",
      classification: "HAZARDOUS",
      definition: "State persistence criteria - halogenated organic compounds (HOC)",
    },
    {
      value: "WP03",
      classification: "ACUTELY_HAZARDOUS",
      definition: "State persistence criteria - polycyclic aromatic hydrocarbons",
    },
    {
      value: "WPCB",
      classification: "HAZARDOUS",
      definition: "Washington State PCB listing",
    },
    {
      value: "WSC2",
      classification: "HAZARDOUS",
      definition: "Washington state solid corrosive",
    },
    {
      value: "WT01",
      classification: "ACUTELY_HAZARDOUS",
      definition: "State Toxic Criteria",
    },
    {
      value: "WT02",
      classification: "HAZARDOUS",
      definition: "State Toxic Criteria",
    },
  ],
};

/**
 * States where waste codes are entirely facility-specific with no validation possible.
 * Texas generates unique codes per facility/wastestream — any code format is accepted.
 *
 * Note: distinct from states like Oregon that have both predefined codes AND a dynamic
 * pattern (`OR` + federal U/P code). Oregon's pattern is validated by regex in
 * `isValidStateWasteCode`, not skipped here.
 */
const STATES_WITH_FACILITY_SPECIFIC_CODES: ReadonlySet<string> = new Set(["TX"]);

/**
 * States historically listed as "has waste codes" but with no canonical entries
 * in `STATE_WASTE_CODES`. Preserved here so `stateHasWasteCodes` keeps returning
 * true for these states (matches the legacy `STATES_WITH_WASTE_CODES` array in
 * `constants.ts`); the federal generator-status calculator relies on this answer.
 * ENG-15212 owns the EPA-list reconciliation that decides whether to add real
 * codes here or remove these states from the declared set.
 */
const STATES_DECLARED_WITHOUT_CANONICAL_ENTRIES: ReadonlySet<string> = new Set(["LA", "PA", "WI"]);

/** Pre-built membership set per state, indexed for O(1) lookups by `isValidStateWasteCode`. */
const STATE_WASTE_CODE_VALUES: Readonly<Record<string, ReadonlySet<string>>> = Object.fromEntries(
  Object.entries(STATE_WASTE_CODES).map(([state, entries]) => [
    state,
    new Set(entries.map((entry) => entry.value)),
  ])
);

/** Pre-built classification index per state for `getStateWasteCodeClassification`. */
const STATE_WASTE_CODE_INDEX: Readonly<Record<string, ReadonlyMap<string, StateWasteCodeEntry>>> =
  Object.fromEntries(
    Object.entries(STATE_WASTE_CODES).map(([state, entries]) => [
      state,
      new Map(entries.map((entry) => [entry.value, entry])),
    ])
  );

/**
 * Regex for Oregon's dynamic codes: a federal U/P code optionally prefixed with
 * `OR` or `OR-`. Captures the bare federal suffix so the classifier can route
 * via the federal U/P classifier.
 *
 * Accepted shapes: `U001`, `OR-U001`, `ORU001` (and the same for P-codes). All
 * three reach Oregon's state-codes field through different upstream paths and
 * must resolve to the same classification.
 *
 * Exported so consumers (e.g. encamp-compliance-engine's `isStateHazardous`)
 * share a single source of truth for the pattern — keeping the regex in one
 * place avoids drift if Oregon's accepted shapes change.
 */
export const OR_DYNAMIC_CODE_PATTERN = /^(?:OR-?)?([UP]\d{3})$/;

// --- Texas waste-code format + suspect-code field-issue rules (ENG-15633) ---

/** Texas Waste Code format: 4-char sequence + 3-digit form code + 1-char class. */
export const TX_WASTE_CODE_PATTERN = /^[A-Z0-9]{4}[0-9]{3}[H123]$/;
/** Legible alias of {@link TX_WASTE_CODE_PATTERN}. */
export const VALID_TX_WASTE_CODE_FORMAT = TX_WASTE_CODE_PATTERN;

export type TxWasteCodeRuleKey =
  | "TX_INVALID_WASTE_CODE_FORMAT"
  | "TX_H_CODE_NO_FEDERAL"
  | "TX_TSDF_CODE_NON_TSDF_SITE"
  | "TX_VSQG_CODE_WITH_EPA_ID";

export type TxWasteCodeRuleContext = {
  hasFederalCode: boolean;
  hasActiveEpaId: boolean;
};

// 7-digit + H is always 8 chars, so it implies valid format; TSDF/VSQG patterns
// can match malformed input, so those predicates guard on valid format too —
// that keeps a malformed code firing ONLY TX_INVALID_WASTE_CODE_FORMAT.
const TX_SEVEN_DIGIT_H = /^\d{7}H$/;
const TX_TSDF_H = /^TSDF.*H$/;
const TX_CESQ_OR_VSQG = /^(CESQ|VSQG)/;

/**
 * Marker codes the waste team accepts on TX manifests even though they don't
 * match the TCEQ 8-char format (e.g. vendors report NONINDUS for
 * non-industrial waste). Matched on the trimmed, uppercased code so casing
 * and padding variants from vendor feeds don't re-fire the format rule.
 */
const TX_FORMAT_EXEMPT_CODES: ReadonlySet<string> = new Set(["NONINDUS"]);

/** Pure, individually-testable, CE-agnostic predicates keyed by rule. */
export const TX_STATE_WASTE_CODE_RULES: Record<
  TxWasteCodeRuleKey,
  (code: string, ctx: TxWasteCodeRuleContext) => boolean
> = {
  TX_INVALID_WASTE_CODE_FORMAT: (code) =>
    !TX_FORMAT_EXEMPT_CODES.has(code.trim().toUpperCase()) && !TX_WASTE_CODE_PATTERN.test(code),
  TX_H_CODE_NO_FEDERAL: (code, ctx) => TX_SEVEN_DIGIT_H.test(code) && !ctx.hasFederalCode,
  TX_TSDF_CODE_NON_TSDF_SITE: (code) => TX_WASTE_CODE_PATTERN.test(code) && TX_TSDF_H.test(code),
  TX_VSQG_CODE_WITH_EPA_ID: (code, ctx) =>
    TX_WASTE_CODE_PATTERN.test(code) && TX_CESQ_OR_VSQG.test(code) && ctx.hasActiveEpaId,
};

/** Human-readable description per rule key (shown in the Field Issues UI). */
export const TX_STATE_WASTE_CODE_RULE_DESC: Record<TxWasteCodeRuleKey, string> = {
  TX_INVALID_WASTE_CODE_FORMAT: "Invalid Texas waste code format",
  TX_H_CODE_NO_FEDERAL: "TX hazardous code with no federal code — likely wrong",
  TX_TSDF_CODE_NON_TSDF_SITE:
    "TSDF code indicates waste shipped from a TSDF, but the site is not one",
  TX_VSQG_CODE_WITH_EPA_ID: "VSQG/CESQ code conflicts with having an active EPA ID",
};

/**
 * Human-readable description for a state-waste-code rule key, falling back to
 * the raw key when it is not a known TX rule. Single source of truth for the
 * Field Issues table, the consolidated Issues column, and the per-cell state
 * waste code warning, so the lookup can't drift between those surfaces.
 */
export function describeRuleKey(ruleKey: string): string {
  const descriptions: Record<string, string> = TX_STATE_WASTE_CODE_RULE_DESC;
  return descriptions[ruleKey] ?? ruleKey;
}

const TX_RULE_KEYS = Object.keys(TX_STATE_WASTE_CODE_RULES) as TxWasteCodeRuleKey[];

/** Runs every rule over each code, returning one (code, ruleKey) per firing. */
export function runTxStateWasteCodeRules(
  codes: string[],
  ctx: TxWasteCodeRuleContext
): Array<{ code: string; ruleKey: TxWasteCodeRuleKey }> {
  const fired: Array<{ code: string; ruleKey: TxWasteCodeRuleKey }> = [];
  for (const code of codes) {
    for (const ruleKey of TX_RULE_KEYS) {
      if (TX_STATE_WASTE_CODE_RULES[ruleKey](code, ctx)) {
        fired.push({ code, ruleKey });
      }
    }
  }
  return fired;
}

/** `true` iff the state has predefined or facility-specific state waste codes, or is declared as a waste-code state without canonical entries (LA / PA / WI). */
export function stateHasWasteCodes(state: string | null | undefined): boolean {
  if (!state) return false;
  const upperState = state.toUpperCase();
  return (
    upperState in STATE_WASTE_CODES ||
    STATES_WITH_FACILITY_SPECIFIC_CODES.has(upperState) ||
    STATES_DECLARED_WITHOUT_CANONICAL_ENTRIES.has(upperState)
  );
}

/**
 * `true` iff `code` is a valid state waste code for `state`.
 * Facility-specific states (e.g. TX) accept any code format.
 * Oregon additionally accepts the dynamic `OR[UP]\d{3}` pattern.
 */
export function isValidStateWasteCode(code: string, state: string): boolean {
  const upperState = state.toUpperCase();
  const upperCode = code.toUpperCase();

  if (STATES_WITH_FACILITY_SPECIFIC_CODES.has(upperState)) {
    return true;
  }

  if (!(upperState in STATE_WASTE_CODE_VALUES)) {
    return false;
  }

  if (upperState === "OR" && OR_DYNAMIC_CODE_PATTERN.test(upperCode)) {
    return true;
  }

  if (STATE_WASTE_CODE_VALUES[upperState].has(upperCode)) {
    return true;
  }

  if (upperCode.startsWith(upperState)) {
    const codeWithoutPrefix = upperCode.slice(upperState.length);
    return STATE_WASTE_CODE_VALUES[upperState].has(codeWithoutPrefix);
  }

  return false;
}

/**
 * `true` iff `code` is a recognized state waste code in ANY state's canonical
 * list (or matches Oregon's dynamic `OR[UP]\d{3}` pattern). Accepts the same
 * bare / state-prefixed shapes as {@link isValidStateWasteCode}.
 *
 * Deliberately does NOT honor the facility-specific "accept any code" states
 * (e.g. TX) as wildcards: those make every string valid, which would defeat the
 * point of a cross-state check. Use this to accept out-of-state codes on a
 * filing while still rejecting codes that aren't real anywhere — a facility's
 * own state is still validated (with its wildcards) via `isValidStateWasteCode`.
 */
export function isValidStateWasteCodeInAnyState(code: string): boolean {
  const upperCode = code.toUpperCase();

  if (OR_DYNAMIC_CODE_PATTERN.test(upperCode)) {
    return true;
  }

  for (const [state, values] of Object.entries(STATE_WASTE_CODE_VALUES)) {
    if (values.has(upperCode)) {
      return true;
    }
    if (upperCode.startsWith(state) && values.has(upperCode.slice(state.length))) {
      return true;
    }
  }

  return false;
}

/**
 * Look up the canonical entry for a state waste code value. `null` if not found.
 * Caller is responsible for normalizing the `value` (strip dash, state prefix, casing).
 * For Oregon dynamic codes (`OR[UP]\d{3}`), prefer `classifyOregonDynamicCode`.
 */
export function getStateWasteCodeEntry(state: string, value: string): StateWasteCodeEntry | null {
  return STATE_WASTE_CODE_INDEX[state.toUpperCase()]?.get(value.toUpperCase()) ?? null;
}

/**
 * Returns the classification of a state waste code from the canonical static list,
 * or `null` if unknown. For Oregon's dynamic `OR[UP]\d{3}` codes, use
 * `classifyStateWasteCode` instead — it handles both the static list and the
 * federal-code-routed dynamic codes.
 */
export function getStateWasteCodeClassification(
  state: string,
  value: string
): StateWasteClassification | null {
  return getStateWasteCodeEntry(state, value)?.classification ?? null;
}

/**
 * Classify a state waste code, including Oregon's dynamic `OR[UP]\d{3}` codes.
 *
 * Oregon accepts `OR` + a federal U/P code as a state waste code; classification
 * for those follows the federal classification of the stripped suffix (P-codes
 * are acutely hazardous, U-codes are hazardous). The legacy `StateWasteCode`
 * table held 373 pre-computed `OR{U,P}xxx` rows for this purpose; this helper
 * computes the equivalent in-process so the canonical list can stay tight.
 */
export function classifyStateWasteCode(
  state: string,
  value: string
): StateWasteClassification | null {
  const upperState = state.toUpperCase();
  const upperValue = value.toUpperCase();

  if (upperState === "OR") {
    const match = OR_DYNAMIC_CODE_PATTERN.exec(upperValue);
    if (match) {
      const federalSuffix = match[1];
      if (isFederalAcutelyHazardousCode(federalSuffix)) return "ACUTELY_HAZARDOUS";
      if (isFederalWasteCode(federalSuffix)) return "HAZARDOUS";
      // Fall through to the static lookup. Oregon's static list contains
      // entries that overlap the loosened dynamic pattern (e.g. `P999` is
      // OR's nerve-agent code, HAZARDOUS, and isn't a real federal P-code);
      // we still want those classified per the static list rather than null.
    }
  }

  return getStateWasteCodeClassification(upperState, upperValue);
}

/** All canonical entries for a state (e.g. for CA reverse lookups in chemical fields). */
export function getStateWasteCodes(state: string): readonly StateWasteCodeEntry[] {
  return STATE_WASTE_CODES[state.toUpperCase()] ?? [];
}

/**
 * `true` iff the (state, code) pair is on EPA's published list of state-only
 * hazardous wastes subject to RCRA e-Manifest.
 *
 * Resolution order:
 *  1. Facility-specific states (TX) — EPA confirms state-only wastes exist but
 *     codes are not enumerated. Returns `true` for any code, matching the
 *     "accept any TX code" behavior of `isValidStateWasteCode`.
 *  2. Canonical entry lookup (with state-prefix-stripped fallback, e.g. `CA121`
 *     → `121`) — the entry's explicit `subjectToEManifest` wins; otherwise the
 *     answer is derived from `classification` (NONHAZARDOUS → `false`,
 *     HAZARDOUS / ACUTELY_HAZARDOUS → `true`).
 *  3. Oregon dynamic `OR[UP]\d{3}` — EPA's Oregon list contains every real
 *     federal U/P code prefixed with `OR`; returns `true` iff the federal
 *     suffix resolves to a real federal U/P code (mirrors the dynamic-code
 *     routing in `classifyStateWasteCode`).
 *  4. Unknown — returns `false`.
 *
 * Note on declared-without-canonical-entries states (`LA`, `PA`, `WI`):
 * `stateHasWasteCodes` returns true for these for federal generator-status
 * parity, but they have no canonical entries here, so any code passed for
 * them falls through to branch 4 → `false`. Verified against EPA's published
 * list: PA and WI explicitly *"do not have state-only wastes subject to the
 * RCRA manifest"*; LA's table reads *"Information is unavailable at this
 * time"*, so `false` is the conservative default. If EPA later publishes
 * LA codes, ENG-15212 should add them to `STATE_WASTE_CODES.LA` — the helper
 * will then resolve them correctly via branch 2.
 *
 * EPA source: https://www.epa.gov/e-manifest/state-only-hazardous-waste-subject-resource-conservation-and-recovery-act-manifests
 */
export function isSubjectToEManifest(code: string, state: string): boolean {
  const upperState = state.toUpperCase();
  // Trim before matching so padded inputs (" MA99") resolve like the bare code
  // — the prefix-strip below keys off `startsWith(state)`, which whitespace
  // would defeat (silent under-flag). Parity with `resolveStateCodeLookupKey`.
  const upperCode = code.trim().toUpperCase();

  if (STATES_WITH_FACILITY_SPECIFIC_CODES.has(upperState)) {
    return true;
  }

  const entry =
    getStateWasteCodeEntry(upperState, upperCode) ??
    (upperCode.startsWith(upperState)
      ? // Strip the state prefix and an optional dash so dash-prefixed
        // (`MA-MA99`) and no-dash-prefixed (`MAMA99`) forms both resolve to
        // the bare canonical code (`MA99`).
        getStateWasteCodeEntry(upperState, upperCode.slice(upperState.length).replace(/^-/, ""))
      : null);

  if (entry) {
    return entry.subjectToEManifest ?? entry.classification !== "NONHAZARDOUS";
  }

  if (upperState === "OR") {
    const match = OR_DYNAMIC_CODE_PATTERN.exec(upperCode);
    if (match) {
      const federalSuffix = match[1];
      if (isFederalAcutelyHazardousCode(federalSuffix) || isFederalWasteCode(federalSuffix)) {
        return true;
      }
    }
  }

  return false;
}
